import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, UntypedFormControl} from "@angular/forms";
import {Subject, takeUntil} from "rxjs";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {TableColumn} from "@vex/interfaces/table-column.interface";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {OriginsService} from "@core/services/backend/common/service/origins.service";
import {fadeInUp400ms} from "@vex/animations/fade-in-up.animation";
import {stagger40ms} from "@vex/animations/stagger.animation";
import {Origin} from "@core/services/backend/common/interface/origins";
import {OriginsCreateUpdateComponent} from "@app/pages/administration/origins-create-update/origins-create-update.component";

@Component({
    selector: 'vex-origins',
    templateUrl: './origins.component.html',
    styleUrls: ['./origins.component.scss'],
    animations: [
        fadeInUp400ms,
        stagger40ms
    ]
})
export class OriginsComponent implements OnInit {

    layoutCtrl = new UntypedFormControl('boxed');
    private readonly destroying$ = new Subject<void>();

    originsList: Origin[] = [];
    dataSource: MatTableDataSource<Origin> | null;
    searchForm: FormGroup;
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    columns: TableColumn<Origin>[] = [
        {label: 'GRIDS.ID', property: 'id', type: 'text', visible: false},
        {label: 'GRIDS.NOMBRE', property: 'name', type: 'text', visible: true, cssClasses: ['font-medium']},
        {label: 'GRIDS.ACTIVO', property: 'active', type: 'image', visible: true},
        {label: 'GRIDS.ACCIONES', property: 'actions', type: 'button', visible: true}
    ];

    currentPage = 0;
    pageSize = 20;
    pageSizeOptions: number[] = [10, 20, 50, 100];
    isLoading: boolean = false;

    constructor(
        private origins: OriginsService,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private snackbar: MatSnackBar
    ) {
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource();
        this.searchForm = new FormGroup({
            name: new FormControl()
        });
        this.loadData();
    }

    loadData() {
        const that = this;
        this.isLoading = true;
        this.initializeData().then(() => {
            this.dataSource.data = that.originsList;
            this.isLoading = false;
        });
    }

    private getSearch() {
        return {
            name: this.searchForm.get("name")?.value ? '%' + this.searchForm.get("name")?.value.toString().toUpperCase() + '%' : ''
        };
    }

    async initializeData() {
        const that = this;
        return await new Promise<void>((resolve, reject) => {
            const list = this.origins.list(JSON.stringify(that.getSearch()), that.currentPage, that.pageSize, 'name ASC');
            list.pipe(takeUntil(this.destroying$)).subscribe({
                next: (data: any) => {
                    const rawOrigins = data.data || data || [];
                    // Normalizar los datos: asegurar que siempre tengan 'id' en minúsculas
                    that.originsList = rawOrigins.map((origin: any) => {
                        // Si tiene ID en mayúsculas pero no id en minúsculas, copiarlo
                        if (origin.ID !== undefined && origin.id === undefined) {
                            origin.id = origin.ID;
                        }
                        // Si tiene id, asegurar que sea un número
                        if (origin.id !== undefined) {
                            origin.id = Number(origin.id);
                        }
                        return origin;
                    });
                    that.paginator.pageIndex = that.currentPage;
                    that.paginator.length = data.total_records || data.totalRecords || that.originsList.length;
                    resolve();
                },
                error: (err) => {
                    console.error('Error loading origins:', err);
                    that.originsList = [];
                    resolve();
                }
            });
        });
    }

    pageChanged(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex;
        this.loadData();
    }

    trackByProperty<T>(index: number, column: TableColumn<T>) {
        return column.property;
    }

    get visibleColumns() {
        return this.columns.filter(column => column.visible).map(column => column.property);
    }

    buscar() {
        this.currentPage = 0;
        this.loadData();
    }

    createOrigin() {
        this.dialog.open(OriginsCreateUpdateComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    updateOrigin(origin: Origin) {
        this.dialog.open(OriginsCreateUpdateComponent, {
            data: origin,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    deleteOrigin(origin: Origin) {
        const that = this;
        this.origins.delete(origin).subscribe({
            next(result) {
                that.snackbar.open('Origen eliminado correctamente', '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                that.snackbar.open('Error al eliminar el origen', '', {
                    duration: 3000
                });
            }
        });
    }
}

